import * as React from 'react'
import { message } from 'antd'
import {
  CopyOutlined,
  CheckCircleOutlined,
  CloseSquareOutlined,
} from '@ant-design/icons'
import { path } from '../../../../../libs'
import { DepositEdit } from '../../edit/types'
import { transactionRequestDestinationType } from '../../../const'

type Props = {
  deposit: DepositEdit
}

const Money = ({
  value,
  currency,
}: {
  value?: string | number
  currency?: string
}) => {
  if (!value || !currency) return null
  const num = typeof value === 'number' ? value : parseFloat(value)
  if (Number.isNaN(num)) return null
  const numberStr = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  }).format(num)
  return (
    <>
      {numberStr}
      <span
        style={{ fontSize: '0.6em', fontWeight: 400, marginLeft: 3, opacity: 0.7 }}
      >
        {currency}
      </span>
    </>
  )
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--crm-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 10,
}

const cardStyle: React.CSSProperties = {
  background: 'var(--crm-bg-elevated)',
  border: '1px solid var(--crm-border)',
  borderRadius: 8,
  padding: 14,
}

const dividerStyle: React.CSSProperties = {
  border: 'none',
  borderTop: '1px solid var(--crm-border)',
  margin: '16px 0',
}

const copyBtnStyle: React.CSSProperties = {
  marginLeft: 6,
  cursor: 'pointer',
  fontSize: 16,
  color: 'var(--crm-text-secondary)',
  verticalAlign: 'middle',
}

export const ClientCard = ({ deposit }: Props) => {
  // ── resolve destination (account / wallet / exchanger) ──
  const account = deposit.client.accounts.find(
    (account) => account.accountId === deposit.destinationId,
  )

  let destinationId = String(deposit.destinationId)
  let balance = account?.balance
  let currency = account?.currency
  let isFixRate = account?.isFixRate
  let frontName: string | undefined

  const isWallet =
    deposit.destinationType === transactionRequestDestinationType.wallet() ||
    deposit.destinationType === 'wallet'

  const isExchanger =
    // @ts-ignore — exchanger.id is string, destinationId is number (loose match preserves page.tsx behavior)
    (deposit.client?.exchanger?.id == deposit.destinationId ||
      deposit.gateway?.exchanger?.id) &&
    deposit.destinationType === 'exchanger'

  if (isWallet) {
    const wallet = deposit.client.wallets.find(
      (wallet) => wallet.walletId === deposit.destinationId,
    )
    balance = wallet?.balance
    currency = wallet?.currency
    isFixRate = undefined
  } else if (isExchanger) {
    destinationId = 'Exchanger'
    balance =
      deposit.client?.exchanger?.balance ||
      deposit.gateway?.exchanger?.balance
    currency = 'USD'
    isFixRate = undefined
    frontName =
      deposit.client?.exchanger?.frontName ||
      deposit.gateway?.exchanger?.frontName
  }

  const sectionTitle = isWallet
    ? 'Wallet'
    : isExchanger
    ? 'Exchanger'
    : 'Account'

  const currentOriginHost = window.location.origin
  const handleCopyClientInfo = () => {
    const clientUrl = `${currentOriginHost}/client/${deposit.client.clientId}`
    const plainText = `
Client name: ${deposit.client.fullName}
Client ID: ${deposit.client.clientId}
Email: ${deposit.client.email}
`.trim()

    const htmlText = `
<b>Client name:</b> ${deposit.client.fullName}
<b>Client ID:</b> <a href="${clientUrl}">${deposit.client.clientId}</a>
<b>Email:</b> ${deposit.client.email}
`.trim()

    navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([htmlText], { type: 'text/html' }),
      }),
    ])
    message.success('Client info copied!')
  }

  const handleCopyClientId = () => {
    navigator.clipboard.writeText(deposit.client.clientId)
    message.success('Client ID copied!')
  }

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(deposit.client.email)
    message.success('Email copied!')
  }

  return (
    <>
      {/* ── CLIENT SECTION ── */}
      <div>
        <div style={sectionTitleStyle}>
          Client
          <CopyOutlined
            onClick={handleCopyClientInfo}
            style={{ marginLeft: 8, cursor: 'pointer', fontSize: 11 }}
          />
        </div>
        <div style={cardStyle}>
          {/* Name */}
          <div
            style={{
              fontSize: 20,
              fontWeight: 600,
              fontFamily: 'monospace',
              marginBottom: 4,
            }}
          >
            {deposit.client.fullName}
          </div>

          {/* Email */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 6,
              wordBreak: 'break-all',
            }}
          >
            <span
              style={{
                fontSize: 14,
                color: 'var(--crm-text-secondary)',
                fontFamily: 'monospace',
              }}
            >
              {deposit.client.email}
            </span>
            <CopyOutlined onClick={handleCopyEmail} style={copyBtnStyle} />
          </div>

          {/* ClientId + verified */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <a
                href={path.clients.details(deposit.client.clientId)}
                target="_blank"
                style={{ fontSize: 20, fontFamily: 'monospace', fontWeight: 600 }}
              >
                {deposit.client.clientId} ↗
              </a>
              <CopyOutlined onClick={handleCopyClientId} style={copyBtnStyle} />
            </div>
            {deposit.client.verified ? (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--crm-accent-green, #22c55e)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: 'var(--crm-accent-green, #22c55e)',
                  }}
                />
                Verified
              </span>
            ) : (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--crm-text-secondary)',
                  textTransform: 'uppercase',
                }}
              >
                Unverified
              </span>
            )}
          </div>

          {/* Country */}
          {deposit.client.country && (
            <div
              style={{
                fontSize: 14,
                color: 'var(--crm-text-secondary)',
                marginBottom: 4,
              }}
            >
              {deposit.client.country}
            </div>
          )}

          {(deposit.client.isTest || deposit.client.isPremium) && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {deposit.client.isTest && (
                <span
                  style={{
                    fontSize: 10,
                    padding: '1px 6px',
                    borderRadius: 3,
                    background: 'rgba(168,85,247,0.1)',
                    color: '#a855f7',
                    border: '1px solid rgba(168,85,247,0.3)',
                  }}
                >
                  Test
                </span>
              )}
              {deposit.client.isPremium && (
                <span
                  style={{
                    fontSize: 10,
                    padding: '1px 6px',
                    borderRadius: 3,
                    background: 'rgba(245,158,11,0.1)',
                    color: '#f59e0b',
                    border: '1px solid rgba(245,158,11,0.3)',
                  }}
                >
                  Premium
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <hr style={dividerStyle} />

      {/* ── WALLET / ACCOUNT / EXCHANGER SECTION ── */}
      <div>
        <div style={sectionTitleStyle}>{sectionTitle}</div>
        <div style={cardStyle}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'monospace',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span>
              {!isWallet && !isExchanger
                ? `Account ID: ${destinationId}`
                : isWallet
                ? `Wallet ID: ${destinationId}`
                : frontName
                ? `${destinationId} · ${frontName}`
                : destinationId}
            </span>
            {!isWallet && !isExchanger && (
              <>
                <span style={{ fontWeight: 400 }}>FixRate</span>
                {isFixRate ? (
                  <CheckCircleOutlined className={'text-success'} />
                ) : (
                  <CloseSquareOutlined className={'text-danger'} />
                )}
              </>
            )}
          </div>
          <div>
            <div
              style={{
                fontSize: 14,
                color: 'var(--crm-text-secondary)',
                marginBottom: 2,
              }}
            >
              Balance
            </div>
            <div
              style={{ fontWeight: 600, fontSize: 20, fontFamily: 'monospace' }}
            >
              <Money value={balance as string} currency={currency} />
            </div>
          </div>
        </div>
        <a
          target="_blank"
          href={`${path.billing.deposit.entry()}?clientId=${deposit.client.clientId}`}
          style={{ display: 'inline-block', fontSize: 14, marginTop: 8 }}
        >
          View deposits ↗
        </a>
      </div>

      {/* ── CLIENT AMOUNT SECTION ── */}
      {deposit.clientAmount && deposit.clientCurrency && (
        <>
          <hr style={dividerStyle} />
          <div>
            <div style={sectionTitleStyle}>Client amount</div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                fontFamily: 'monospace',
                color: 'var(--crm-accent-green, #22c55e)',
              }}
            >
              <Money
                value={deposit.clientAmount}
                currency={deposit.clientCurrency}
              />
            </div>
          </div>
        </>
      )}
    </>
  )
}
